import { useEffect, useState } from 'react';
import FormulaireConnexion, { EcranRedirection } from './FormulaireConnexion';
import AppPromo from '../layout/AppPromo';
import { useTranslation } from '../../context/LanguageContext';
import { useDomain } from '../../context/DomainContext';
import { getAppDescription } from '../../services/appsService';
import { paletteApp } from '../../utils/appPalette';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * AppLandingPage - Page publique d'une adresse d'application autonome
 * (depenses.cabaza.eu…), affichée à la place de la vitrine de Cabaza : le visiteur
 * y voit SON app (icône, nom, couleur, description) et le formulaire de connexion.
 * Sans connexion, rien d'autre de la plateforme n'est présenté, hormis l'invitation à
 * découvrir Cabaza et les autres apps (AppPromo).
 *
 * Les teintes de Cabaza (--color-cabaza-purple / -pink) sont redéfinies sur le conteneur :
 * le formulaire de connexion existant prend ainsi les couleurs du module sans être dupliqué.
 */
const AppLandingPage = () => {
    const { t, language } = useTranslation();
    const { moduleCode, app } = useDomain();
    const tl = t.appLanding || {};

    const [description, setDescription] = useState('');
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getAppDescription(moduleCode, language).then((d) => { if (!cancelled) setDescription(d); });
        return () => { cancelled = true; };
    }, [moduleCode, language]);

    const palette = paletteApp(app.themeColor);
    const nom = app.name || moduleCode;
    const iconeUrl = app.iconPath && /^[a-zA-Z0-9_\-/.]+$/.test(app.iconPath) && !app.iconPath.includes('..')
        ? `${FILES_BASE_URL}/${app.iconPath}`
        : null;

    const style = palette ? {
        '--color-cabaza-purple': palette.primary,
        '--color-cabaza-pink': palette.secondary,
        background: `linear-gradient(180deg, ${palette.soft} 0%, #ffffff 60%)`
    } : { background: '#ffffff' };

    if (isRedirecting) {
        return <div style={palette ? { '--color-cabaza-purple': palette.primary, '--color-cabaza-pink': palette.secondary } : undefined}><EcranRedirection /></div>;
    }

    return (
        <div className="min-h-dvh flex flex-col" style={style}>
            <main className="flex-1 flex items-center justify-center px-4 py-10">
                <div className="w-full max-w-md">
                    <div className="text-center mb-7">
                        {iconeUrl ? (
                            <img src={iconeUrl} alt="" className="w-24 h-24 mx-auto rounded-[22%] shadow-lg object-cover" />
                        ) : (
                            <div className="w-24 h-24 mx-auto rounded-[22%] shadow-lg flex items-center justify-center text-4xl font-bold text-white bg-gradient-to-br from-cabaza-purple to-cabaza-pink">
                                {nom.charAt(0).toUpperCase()}
                            </div>
                        )}
                        <h1 className="mt-5 text-3xl font-extrabold tracking-tight bg-gradient-to-r from-cabaza-purple to-cabaza-pink bg-clip-text text-transparent">{nom}</h1>
                        {description && <p className="mt-2 text-base text-gray-600">{description}</p>}
                    </div>

                    {/* Le formulaire porte déjà sa propre carte : pas de seconde enveloppe */}
                    <p className="mb-3 text-center text-sm font-semibold text-gray-700">
                        {(tl.signIn || 'Connectez-vous pour accéder à {name}').replace('{name}', nom)}
                    </p>
                    <FormulaireConnexion onRedirection={() => setIsRedirecting(true)} />

                    <AppPromo />

                    <p className="mt-6 text-center text-xs text-gray-400">{tl.poweredBy || 'Une application Cabaza'}</p>
                </div>
            </main>
        </div>
    );
};

export default AppLandingPage;
